import { useRef } from 'react';
import { useParams } from 'react-router-dom';
import type { PdfDocumentType } from '@domain/schemas/contract.schema';
import { pdfDocumentQueries } from '@queries/getPdfDocument';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

export const PDF_REFETCH_INTERVAL_MS = 3000;
// Poll cap for a regenerated PDF (e.g. async malware scan); enforced by the caller's awaiting window.
export const PDF_REFETCH_TIMEOUT_MS = 120_000;

interface StableUrl {
  key?: string;
  version?: number;
  url?: string;
}

/**
 * The download endpoint re-signs the S3 URL on every call, so swapping it each poll remounts
 * the viewer. Only adopt a new URL when `documentVersion` changes — not on signature churn.
 * `key` is the query identity: on a change, drop the old URL (versions across resources may
 * collide); while it still serves the previous query's keepPreviousData placeholder, hold nothing.
 */
export const selectStableUrl = (
  prev: StableUrl,
  key: string,
  isPlaceholderData: boolean,
  documentVersion: number | undefined,
  freshUrl?: string,
): StableUrl => {
  if (key !== prev.key) {
    return isPlaceholderData ? { key } : { key, version: documentVersion, url: freshUrl };
  }
  if (documentVersion !== undefined && documentVersion !== prev.version) {
    return { key, version: documentVersion, url: freshUrl };
  }
  return prev;
};

export const usePullPdfDocumentUrl = (
  resourceName: 'templates' | 'submissions' | 'endorsements' | 'brandings',
  pdfDocumentType: PdfDocumentType,
  expectedVersion: number,
  enabled: boolean = true,
  refetchWhilePending: boolean = false,
) => {
  const { id = '' } = useParams<{ id: string }>();
  const { endorsement_id = '' } = useParams<{ endorsement_id: string }>();
  const stableUrlRef = useRef<StableUrl>({});

  const documentsQuery = useQuery({
    ...pdfDocumentQueries.forDocument({
      id,
      endorsementId: endorsement_id,
      resourceName,
      pdfDocumentType,
      expectedVersion,
    }),
    retry: 999,
    retryDelay: 1000,
    gcTime: 60_000,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    enabled,
    refetchInterval: refetchWhilePending ? PDF_REFETCH_INTERVAL_MS : false,
  });

  const documentVersion = documentsQuery?.data?.data?.documentVersion;
  const queryIdentity = `${id}|${endorsement_id}|${resourceName}|${pdfDocumentType}`;
  stableUrlRef.current = selectStableUrl(
    stableUrlRef.current,
    queryIdentity,
    documentsQuery.isPlaceholderData,
    documentVersion,
    documentsQuery?.data?.data?.url,
  );

  return {
    url: stableUrlRef.current.url,
    documentVersion,
    expectedVersion,
    isLoading: documentsQuery.isLoading,
    refetch: documentsQuery.refetch,
    documentPullUpdatedAt: documentsQuery.dataUpdatedAt,
  };
};
